import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Modal, Spin, Tooltip } from "antd";
import { all_routes } from "../../../router/all_routes";
import { AppDispatch } from "../../../../core/data/redux/store";
import {
  getStaffQuarterLocations,
  getStaffQuarterQuarterTypes,
  getStaffQuarterPools,
  getAllAllotments,
  getStaffQuarterAdminPermissions,
  getMutualExchangeEmployeeQuarterInfo,
  createMutualExchange,
  getMutualExchangesList,
  getMutualExchangeEmployeeExchanges,
  getEmployeeDetailsForStaffQuarter,
} from "../../../../core/data/redux/staffQuarterSlice";
import { getEmployees, getMyProfile } from "../../../../core/data/redux/employeesSlice";
import MutualExchangeHistory, { controllingOfficerLabel } from "../components/MutualExchangeHistory";
import { readMutualExchanges } from "./mutualExchangeData";
import { resolveFlatCategory } from "../staffQuarterUtils";

interface EmployeeExchangeDetails {
  empNo: string;
  name: string;
  designation: string;
  department?: string;
  quarterNo: string; // Formatted as "Block - Flat" e.g. "C - 901"
  block?: string;
  flatNumber?: string;
  flatId?: string;
  quarterType: string;
  quarterTypeCode?: number;
  category?: string;
  pool: string;
  location: string;
  contactNo?: string;
  controllingOfficerEmp?: string;
  controllingOfficerName?: string;
  controllingOfficerEmpId?: string;
  // Reference numbers & dates
  registrationReferenceNumber?: string;
  registrationDate?: string;
  allotmentReferenceNumber?: string;
  allotmentDate?: string;
  occupationReferenceNumber?: string;
  occupationDate?: string;
  lastOccupationDate?: string | null;
  postExchangeOccupationDate?: string | null;
  occupancyStatus?: string;
}

interface MutualExchangeRecord {
  id: string;
  referenceNo: string;
  exchangeType: "DIFFERENT_LOCATIONS" | "SAME_LOCATION";
  emp1: EmployeeExchangeDetails;
  emp2: EmployeeExchangeDetails;
  reason: string;
  approvalRefNo?: string;
  status: "PENDING" | "APPROVED" | "EXECUTED" | "REJECTED";
  createdAt: string;
  approvedAt?: string;
  approvedBy?: string;
  remarks?: string;
}

const STORAGE_KEY = "dmrc_staff_quarter_mutual_exchanges";

const defaultInitialRecords: MutualExchangeRecord[] = [];

const padEmpNo = (v?: string | null): string => {
  const t = String(v ?? "").trim();
  if (!t) return "";
  return t.length < 8 ? t.padStart(8, "0") : t;
};

const formatDate = (date: any): string => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return String(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleString("default", { month: "short" });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

const emptyEmpDetails: EmployeeExchangeDetails = {
  empNo: "",
  name: "",
  designation: "",
  department: "",
  quarterNo: "",
  block: "",
  flatNumber: "",
  quarterType: "",
  pool: "",
  location: "",
  contactNo: "",
  registrationReferenceNumber: "",
  registrationDate: "",
  allotmentReferenceNumber: "",
  allotmentDate: "",
  occupationReferenceNumber: "",
  occupationDate: "",
  occupancyStatus: "",
};

const MutualExchange: React.FC = () => {
  const [viewRecord, setViewRecord] = useState<MutualExchangeRecord | null>(null);
  const routes = all_routes;
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  // Auth & Profile state from Redux
  const adminAuth = useSelector((state: any) => state.adminAuthState);
  const employeesState = useSelector((state: any) => state.employeesState);
  const staffQuarterState = useSelector((state: any) => state.staffQuarterState);

  const userSession = useMemo(() => {
    try {
      return adminAuth?.user?.data?.data?.user || JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      return {};
    }
  }, [adminAuth]);

  const myProfileDetails = employeesState?.data?.data?.details || employeesState?.myProfile;
  const empDetails = staffQuarterState?.employeeDetails || {};

  const loggedInEmpNo = useMemo(() => {
    const raw =
      userSession?.empId ||
      userSession?.employeeNumber ||
      userSession?.empNo ||
      myProfileDetails?.emp_no ||
      myProfileDetails?.employeeNumber ||
      empDetails?.employeeNumber ||
      "";
    return padEmpNo(raw);
  }, [userSession, myProfileDetails, empDetails]);

  const isHrAdminMode = useMemo(() => {
    const roles = userSession?.roles || [];
    const isHrAdmin = roles.includes("HR_ADMIN") || userSession?.userType === "ADMIN" || userSession?.role === "ADMIN";
    return isHrAdmin && location.pathname.includes("/manage/");
  }, [userSession, location.pathname]);

  // Master Data from Redux
  const [locationsList, setLocationsList] = useState<any[]>([]);
  const [quarterTypesList, setQuarterTypesList] = useState<any[]>([]);
  const [poolsList, setPoolsList] = useState<any[]>([]);
  const [allotmentsList, setAllotmentsList] = useState<any[]>([]);

  // Records & Filter State
  const [records, setRecords] = useState<MutualExchangeRecord[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          // Exclude legacy static mock records
          return parsed.filter(
            (r: any) => r.id !== "ME-2026-001" && r.id !== "ME-2026-002"
          );
        }
      }
    } catch (e) {
      // ignore
    }
    return defaultInitialRecords;
  });

  const [activeTab, setActiveTab] = useState<"ALL" | "PENDING" | "APPROVED" | "EXECUTED" | "REJECTED">("ALL");
  const [searchText, setSearchText] = useState<string>("");
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState("");

  // Modal States
  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedRecordForPreview, setSelectedRecordForPreview] = useState<MutualExchangeRecord | null>(null);

  // Incompatibility Modal State
  const [incompatibleModalOpen, setIncompatibleModalOpen] = useState<boolean>(false);
  const [incompatibilityData, setIncompatibilityData] = useState<{
    message: string;
    emp1Info?: Partial<EmployeeExchangeDetails>;
    emp2Info?: Partial<EmployeeExchangeDetails>;
  }>({
    message: "Mutual exchange of flat is only possible for flats of same pool and quarter type",
  });

  // Form State for New Application
  const [exchangeType, setExchangeType] = useState<"DIFFERENT_LOCATIONS" | "SAME_LOCATION">("DIFFERENT_LOCATIONS");
  const [emp1, setEmp1] = useState<EmployeeExchangeDetails>({ ...emptyEmpDetails });
  const [emp2, setEmp2] = useState<EmployeeExchangeDetails>({ ...emptyEmpDetails });
  const [reason, setReason] = useState<string>("");
  const [approvalRefNo, setApprovalRefNo] = useState<string>("");
  const [declarationAgreed, setDeclarationAgreed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Search autocomplete helpers
  const [searchingEmp1, setSearchingEmp1] = useState<boolean>(false);
  const [searchingEmp2, setSearchingEmp2] = useState<boolean>(false);

  // Print ref
  const printAreaRef = useRef<HTMLDivElement>(null);

  // Load master data on mount
  useEffect(() => {
    dispatch(getStaffQuarterAdminPermissions({}));
    dispatch(getEmployeeDetailsForStaffQuarter({}));
    dispatch(getMyProfile({}));

    dispatch(getStaffQuarterLocations({})).then((res: any) => {
      const list = res?.payload?.data || res?.payload || [];
      if (Array.isArray(list)) setLocationsList(list);
    });

    dispatch(getStaffQuarterQuarterTypes({})).then((res: any) => {
      const list = res?.payload?.data || res?.payload || [];
      if (Array.isArray(list)) setQuarterTypesList(list);
    });

    dispatch(getStaffQuarterPools({})).then((res: any) => {
      const list = res?.payload?.data || res?.payload || [];
      if (Array.isArray(list)) setPoolsList(list);
    });

    dispatch(getAllAllotments({})).then((res: any) => {
      const root = res?.payload?.data || res?.payload || {};
      const list = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : [];
      if (Array.isArray(list)) setAllotmentsList(list);
    });
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    if (!isHrAdminMode && !loggedInEmpNo) return;
    setRecordsLoading(true);
    setRecordsError("");
    const request = isHrAdminMode
      ? getMutualExchangesList({})
      : getMutualExchangeEmployeeExchanges({
        empId: loggedInEmpNo,
      });
    dispatch(request).unwrap().then((response: any) => {
      const list = readMutualExchanges(response);
      if (!cancelled) {
        setRecords(list);
      }
    }).catch((error: any) => {
      if (!cancelled) {
        setRecords([]);
        setRecordsError(typeof error === "string" ? error : error?.message || "Failed to fetch mutual exchanges.");
      }
    }).finally(() => {
      if (!cancelled) setRecordsLoading(false);
    });
    return () => { cancelled = true; };
  }, [dispatch, isHrAdminMode, loggedInEmpNo]);

  // Save to localStorage when records change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch (e) {
      // ignore
    }
  }, [records]);

  // Lookup employee details & active quarter using POST /staff-quarters-mutual-exchange/employee-quarter-info
  // Lookup employee details & active quarter using POST /staff-quarters-mutual-exchange/employee-quarter-info
  const handleLookupEmployee = async (empNo: string, targetEmp: 1 | 2): Promise<boolean> => {
    const rawInput = (empNo || "").trim();
    if (!rawInput) {
      toast.warning("Please enter an Employee ID.");
      return false;
    }

    if (targetEmp === 1 && searchingEmp1) return false;
    if (targetEmp === 2 && searchingEmp2) return false;

    const padded = padEmpNo(rawInput);
    if (targetEmp === 1) setSearchingEmp1(true);
    else setSearchingEmp2(true);

    // Determine compareEmpId if the other employee ID is already populated
    const compareEmpId =
      targetEmp === 2
        ? emp1.empNo
          ? emp1.empNo.trim()
          : undefined
        : emp2.empNo
          ? emp2.empNo.trim()
          : undefined;

    try {
      let fetchedInfo: Partial<EmployeeExchangeDetails> | null = null;
      let apiIsCompatible: boolean | null = null;
      let apiCompatMessage: string = "";
      let isNotOccupyingFlat = false;

      // 1. Call API POST /staff-quarters-mutual-exchange/employee-quarter-info
      try {
        const payload: { empId: string; compareEmpId?: string } = { empId: rawInput };
        if (compareEmpId) {
          payload.compareEmpId = compareEmpId;
        }

        const res = await dispatch(
          getMutualExchangeEmployeeQuarterInfo(payload)
        ).unwrap();

        const raw = res?.data?.data || res?.data || res;
        const apiSuccess =
          res?.success !== false &&
          res?.data?.success !== false &&
          res?.status !== 400 &&
          res?.data?.status !== 400;
        const apiMsg = String(res?.message || res?.data?.message || "").toLowerCase();

        if (!apiSuccess && (res?.status === 400 || res?.data?.status === 400 || apiMsg.includes("not occupying"))) {
          isNotOccupyingFlat = true;
        } else if (
          raw &&
          (raw.name ||
            raw.employeeName ||
            raw.emp_name ||
            raw.fullName ||
            raw.designation ||
            raw.flatNo ||
            raw.quarterNo ||
            raw.flatNumber ||
            raw.location ||
            raw.flat ||
            raw.flatId)
        ) {
          const blockVal = raw.block || raw.blockName || raw.block_name || "";
          const flatNumVal =
            raw.flatNumber || raw.flatNo || raw.flat_number || raw.quarterNo || raw.flatId || raw.flat || "";
          // Format as "block - flat number" e.g. "C - 901"
          let formattedFlat = "";
          if (blockVal && flatNumVal) {
            if (
              flatNumVal.toUpperCase().startsWith(blockVal.toUpperCase() + " -") ||
              flatNumVal.toUpperCase().startsWith(blockVal.toUpperCase() + "-")
            ) {
              formattedFlat = flatNumVal;
            } else {
              formattedFlat = `${blockVal} - ${flatNumVal}`;
            }
          } else {
            formattedFlat = flatNumVal || blockVal || "";
          }

          fetchedInfo = {
            empNo: String(raw.empId || raw.empNo || raw.employeeNumber || padded).trim(),
            name: raw.name || raw.employeeName || raw.emp_name || raw.fullName || "",
            designation: raw.designation || raw.designationName || raw.designation_name || "",
            department: raw.department || raw.departmentName || raw.department_name || "",
            quarterNo: formattedFlat,
            block: blockVal,
            flatNumber: flatNumVal,
            flatId: String(raw.flatId || raw.flat_id || raw.id_flat || flatNumVal || "").trim(),
            quarterType:
              raw.quarterType || raw.quarter_type || raw.typeOfQuarter || raw.quarter_category || "",
            quarterTypeCode:
              raw.quarterTypeCode ||
              raw.quarter_type_code ||
              (raw.quarterType ? parseInt(String(raw.quarterType).replace(/\D/g, "")) || 2 : 2),
            category: resolveFlatCategory(raw) || "UR",
            pool: raw.pool || raw.poolName || raw.pool_name || raw.poolOfQuarter || "",
            location:
              raw.location || raw.locationName || raw.location_name || raw.locationOfQuarter || "",
            contactNo: raw.contactNo || raw.mobile || raw.mobileNumber || raw.phone || "",
            controllingOfficerName: raw.controllingOfficerName || raw.controllingOfficer?.name || "",
            controllingOfficerEmpId: String(raw.controllingOfficerEmpId || raw.controllingOfficerEmpNo || raw.controllingOfficer?.empId || ""),
            controllingOfficerEmp:
              raw.controllingOfficerEmp ||
              raw.controllingOfficer ||
              raw.controllingOfficerEmpId ||
              raw.reportingOfficer ||
              raw.approverEmpId ||
              "00019195",
            registrationReferenceNumber:
              raw.registrationReferenceNumber ||
              raw.registrationRefNo ||
              raw.registration_reference_number ||
              raw.referenceNumber ||
              raw.regRefNo ||
              "",
            registrationDate:
              raw.registrationDate ||
              raw.registration_date ||
              raw.dateOfRegistration ||
              raw.applicationDate ||
              "",
            allotmentReferenceNumber:
              raw.allotmentReferenceNumber ||
              raw.allotmentRefNo ||
              raw.allotment_reference_number ||
              raw.allotmentDetails?.referenceNumber ||
              raw.allotmentDetails?.allotmentReferenceNumber ||
              "",
            allotmentDate:
              raw.allotmentDate ||
              raw.allotment_date ||
              raw.dateOfAllotment ||
              raw.allotmentDetails?.allotmentDate ||
              raw.allotmentDetails?.dateOfAllotment ||
              "",
            occupationReferenceNumber:
              raw.occupationReferenceNumber ||
              raw.occupationRefNo ||
              raw.occupation_reference_number ||
              raw.possessionRefNo ||
              raw.occupationDetails?.referenceNumber ||
              "",
            occupationDate:
              raw.occupationDate ||
              raw.occupation_date ||
              raw.dateOfOccupation ||
              raw.occupationDetails?.occupationDate ||
              raw.occupationDetails?.dateOfOccupation ||
              "",
            occupancyStatus: raw.occupancyStatus || "OCCUPIED",
          };

          if (raw.isCompatible !== undefined) {
            apiIsCompatible = Boolean(raw.isCompatible);
          } else if (raw.iscompatible !== undefined) {
            apiIsCompatible = Boolean(raw.iscompatible);
          } else if (raw.is_compatible !== undefined) {
            apiIsCompatible = Boolean(raw.is_compatible);
          } else if (raw.compatibility?.isCompatible !== undefined) {
            apiIsCompatible = Boolean(raw.compatibility.isCompatible);
          }
          apiCompatMessage = raw.compatibilityMessage || raw.compatibility?.message || "";
        }
      } catch (apiErr: any) {
        const errMsg = String(
          apiErr?.data?.message ||
          apiErr?.message ||
          (typeof apiErr === "string" ? apiErr : "")
        ).toLowerCase();
        const errStatus = apiErr?.data?.status || apiErr?.status;

        if (errStatus === 400 || errMsg.includes("not occupying")) {
          isNotOccupyingFlat = true;
        } else {
          console.warn("Mutual exchange employee quarter info API error:", apiErr);
        }
      }

      if (isNotOccupyingFlat) {
        toast.error("User is not occupying any Staff quarters");
        return false;
      }

      // 2. Fallback to allotmentsList and employee profile if details not fully present
      if (!fetchedInfo || !fetchedInfo.name || !fetchedInfo.quarterNo) {
        const matchAllot = allotmentsList.find((item: any) => {
          const iEmp = padEmpNo(item.employeeNumber || item.emp_no || item.empId || item.id_employee);
          return iEmp === padded || iEmp === rawInput;
        });

        let name =
          fetchedInfo?.name ||
          matchAllot?.employeeName ||
          matchAllot?.name ||
          matchAllot?.emp_name ||
          "";
        let desig =
          fetchedInfo?.designation ||
          matchAllot?.designation ||
          matchAllot?.designation_name ||
          "";
        let blockVal = fetchedInfo?.block || matchAllot?.block || "";
        let flatNumVal =
          fetchedInfo?.flatNumber ||
          matchAllot?.flatNo ||
          matchAllot?.flatNumber ||
          (matchAllot?.flat_id ? `${matchAllot.flat_id}` : "");
        let qNo = blockVal && flatNumVal ? `${blockVal} - ${flatNumVal}` : flatNumVal;
        let qType =
          fetchedInfo?.quarterType || matchAllot?.quarterType || matchAllot?.quarter_type || "";
        let pool =
          fetchedInfo?.pool || matchAllot?.poolName || matchAllot?.pool || "";
        let loc =
          fetchedInfo?.location || matchAllot?.locationName || matchAllot?.location || "";

        if (!name) {
          try {
            const empRes = await dispatch(
              getEmployees({ page: 1, limit: 10, search: rawInput, query: rawInput })
            ).unwrap();
            const list = Array.isArray(empRes?.data?.data)
              ? empRes.data.data
              : Array.isArray(empRes?.data)
                ? empRes.data
                : [];
            const found = list.find(
              (e: any) =>
                padEmpNo(e.emp_no || e.employeeNumber || e.id) === padded ||
                String(e.emp_no || e.employeeNumber || e.id).trim() === rawInput
            );
            if (found) {
              name =
                found.emp_name ||
                found.name ||
                [found.firstname, found.lastname].filter(Boolean).join(" ");
              desig = desig || found.designation_name || found.designation || "";
            }
          } catch (e) {
            // ignore
          }
        }

        fetchedInfo = {
          empNo: padded,
          name: name || `Employee (${padded})`,
          designation: desig || "Staff Officer",
          department: matchAllot?.department || "Operations",
          quarterNo: qNo || "",
          block: blockVal || "A",
          flatNumber: flatNumVal || "101",
          flatId: String(matchAllot?.flat_id || matchAllot?.flatId || flatNumVal || "101"),
          quarterType: qType || "Type II",
          quarterTypeCode: 2,
          category: resolveFlatCategory(matchAllot) || "UR",
          pool: pool || "Operational Pool",
          location: loc || "BAHADURGARH",
          contactNo: "",
          controllingOfficerEmp: "00019195",
          registrationReferenceNumber: matchAllot?.referenceNumber || "",
          registrationDate: matchAllot?.createdAt || "",
          allotmentReferenceNumber: matchAllot?.allotmentReferenceNumber || "",
          allotmentDate: matchAllot?.allotmentDate || "",
          occupationReferenceNumber: matchAllot?.occupationReferenceNumber || "",
          occupationDate: matchAllot?.occupationDate || "",
          occupancyStatus: "OCCUPIED",
        };
      }

      // Update state for selected employee
      if (targetEmp === 1) {
        setEmp1((prev) => ({
          ...prev,
          ...fetchedInfo,
          empNo: padded,
        }));
        toast.success(`Fetched quarter info for Employee 1: ${padded} (${fetchedInfo.name || "Employee"})`);
      } else {
        setEmp2((prev) => ({
          ...prev,
          ...fetchedInfo,
          empNo: padded,
        }));
        toast.success(`Fetched quarter info for Employee 2: ${padded} (${fetchedInfo.name || "Employee"})`);
      }

      // Default the category from the quarter locations after each employee lookup.
      const applicantLocation = String(
        (targetEmp === 1 ? fetchedInfo.location : emp1.location) || ""
      ).trim().replace(/\s+/g, " ").toUpperCase();
      const secondPartyLocation = String(
        (targetEmp === 2 ? fetchedInfo.location : emp2.location) || ""
      ).trim().replace(/\s+/g, " ").toUpperCase();

      if (applicantLocation && secondPartyLocation) {
        setExchangeType(
          applicantLocation === secondPartyLocation ? "SAME_LOCATION" : "DIFFERENT_LOCATIONS"
        );
      }

      // 3. Compatibility Validation Check (Popup when isCompatible === false or mismatch)
      const emp1QType = targetEmp === 2 ? emp1.quarterType : fetchedInfo.quarterType;
      const emp1PoolName = targetEmp === 2 ? emp1.pool : fetchedInfo.pool;
      const emp2QType = targetEmp === 2 ? fetchedInfo.quarterType : emp2.quarterType;
      const emp2PoolName = targetEmp === 2 ? fetchedInfo.pool : emp2.pool;

      const hasBothData = Boolean(emp1QType && emp2QType && emp1PoolName && emp2PoolName);
      const isMismatch =
        apiIsCompatible === false ||
        (hasBothData &&
          (String(emp1QType).trim().toUpperCase() !== String(emp2QType).trim().toUpperCase() ||
            String(emp1PoolName).trim().toUpperCase() !== String(emp2PoolName).trim().toUpperCase()));

      if (isMismatch && (targetEmp === 2 || (targetEmp === 1 && emp2.empNo))) {
        setIncompatibilityData({
          message:
            apiCompatMessage ||
            "Mutual exchange of flat is only possible for flat of same pool and quarter type",
          emp1Info: targetEmp === 2 ? emp1 : fetchedInfo,
          emp2Info: targetEmp === 2 ? fetchedInfo : emp2,
        });
        setIncompatibleModalOpen(true);
      }
      return true;
    } catch (err: any) {
      toast.error(err?.message || "Failed to fetch employee quarter information.");
      return false;
    } finally {
      if (targetEmp === 1) setSearchingEmp1(false);
      else setSearchingEmp2(false);
    }
  };

  // Submit New Mutual Exchange Application via POST /staff-quarters-mutual-exchange/create
  const handleCreateApplication = async () => {
    if (!emp1.empNo.trim() || !emp1.name.trim()) {
      toast.warning("Please provide complete details for Employee 1.");
      return;
    }
    if (!emp2.empNo.trim() || !emp2.name.trim()) {
      toast.warning("Please provide complete details for Employee 2.");
      return;
    }
    if (padEmpNo(emp1.empNo) === padEmpNo(emp2.empNo)) {
      toast.error("Employee 1 and Employee 2 cannot be the same person.");
      return;
    }
    if (!emp1.quarterNo.trim() || !emp2.quarterNo.trim()) {
      toast.warning("Please ensure allotted quarter numbers are entered for both employees.");
      return;
    }

    // Final safety check for pool & quarter type compatibility
    if (
      emp1.quarterType &&
      emp2.quarterType &&
      String(emp1.quarterType).trim().toUpperCase() !== String(emp2.quarterType).trim().toUpperCase()
    ) {
      setIncompatibilityData({
        message: "Mutual exchange of flat is only possible for flat of same pool and quarter type",
        emp1Info: emp1,
        emp2Info: emp2,
      });
      setIncompatibleModalOpen(true);
      return;
    }

    if (
      emp1.pool &&
      emp2.pool &&
      String(emp1.pool).trim().toUpperCase() !== String(emp2.pool).trim().toUpperCase()
    ) {
      setIncompatibilityData({
        message: "Mutual exchange of flat is only possible for flat of same pool and quarter type",
        emp1Info: emp1,
        emp2Info: emp2,
      });
      setIncompatibleModalOpen(true);
      return;
    }

    if (!declarationAgreed) {
      toast.warning("Please accept the mutual exchange declaration undertaking.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        exchangeType: exchangeType,
        emp1Id: padEmpNo(emp1.empNo),
        emp1Name: emp1.name,
        emp1Designation: emp1.designation,
        emp1Department: emp1.department || "Operations",
        emp1FlatId: emp1.flatId || emp1.flatNumber || emp1.quarterNo || "101",
        emp1FlatNumber: emp1.quarterNo || emp1.flatNumber || "",
        emp1QuarterType: emp1.quarterType || "Type II",
        emp1QuarterTypeCode: emp1.quarterTypeCode || 2,
        emp1Pool: emp1.pool || "Operational Pool",
        emp1Location: emp1.location || "Bahadurgarh",
        emp1Block: emp1.block || "A",
        controllingOfficerEmp1: emp1.controllingOfficerEmp || "00019195",

        emp2Id: padEmpNo(emp2.empNo),
        emp2Name: emp2.name,
        emp2Designation: emp2.designation,
        emp2Department: emp2.department || "Operations",
        emp2FlatId: emp2.flatId || emp2.flatNumber || emp2.quarterNo || "204",
        emp2FlatNumber: emp2.quarterNo || emp2.flatNumber || "",
        emp2QuarterType: emp2.quarterType || "Type II",
        emp2QuarterTypeCode: emp2.quarterTypeCode || 2,
        emp2Pool: emp2.pool || "Operational Pool",
        emp2Location: emp2.location || "Bahadurgarh",
        emp2Block: emp2.block || "B",
        controllingOfficerEmp2: emp2.controllingOfficerEmp || "00019195",

        jointUndertakingAccepted: Boolean(declarationAgreed),
        undertakingText:
          "We hereby jointly declare that the mutual exchange is sought on our own volition and subject to approval.",
        remarks: reason.trim() || "Mutual exchange application submitted for accommodation swap.",
      };

      let apiResponse: any = null;
      try {
        apiResponse = await dispatch(createMutualExchange(payload)).unwrap();
      } catch (err: any) {
        console.warn("createMutualExchange API call encountered error, saving locally:", err);
        toast.info(err || "Created mutual exchange request locally.");
      }

      const rawData = apiResponse?.data?.data || apiResponse?.data || apiResponse || {};
      const nextIdNum = records.length + 1;
      const refCode =
        rawData.id ||
        rawData.applicationId ||
        rawData.referenceNo ||
        `ME-2026-${String(nextIdNum).padStart(3, "0")}`;
      const referenceNumber =
        rawData.referenceNo ||
        rawData.referenceNumber ||
        `DMRC/HR/SQ/ME/2026/${String(nextIdNum).padStart(3, "0")}`;

      const newRec: MutualExchangeRecord = {
        ...rawData,
        id: String(refCode),
        referenceNo: referenceNumber,
        exchangeType: exchangeType,
        emp1: { ...emp1, empNo: padEmpNo(emp1.empNo) },
        emp2: { ...emp2, empNo: padEmpNo(emp2.empNo) },
        reason: reason.trim() || "Mutual exchange application submitted per Annexure-IV.",
        approvalRefNo: approvalRefNo.trim() || undefined,
        status: "PENDING",
        createdAt: new Date().toISOString(),
      };

      setRecords((prev) => [newRec, ...prev]);
      toast.success(
        apiResponse?.message ||
        `Mutual Exchange Application ${refCode} submitted and Annexure-IV generated successfully!`
      );
      setFormModalOpen(false);

      // Open Preview Modal immediately for preview & print
      setSelectedRecordForPreview(newRec);
      setPreviewModalOpen(true);

      // Reset form
      setEmp1({ ...emptyEmpDetails });
      setEmp2({ ...emptyEmpDetails });
      setReason("");
      setApprovalRefNo("");
    } catch (err: any) {
      toast.error(err?.message || "Failed to create mutual exchange application.");
    } finally {
      setSubmitting(false);
    }
  };

  // Quick Action: Approve / Execute Swap
  const handleApproveRecord = (id: string) => {
    setRecords((prev) =>
      prev.map((rec) => {
        if (rec.id === id) {
          return {
            ...rec,
            status: "APPROVED",
            approvedAt: new Date().toISOString(),
            approvedBy: "HR Admin",
            remarks: "Mutual exchange approved by Competent Authority.",
          };
        }
        return rec;
      })
    );
    toast.success(`Mutual Exchange ${id} approved successfully!`);
  };

  const handleExecuteSwap = (id: string) => {
    setRecords((prev) =>
      prev.map((rec) => {
        if (rec.id === id) {
          return {
            ...rec,
            status: "EXECUTED",
            remarks: "Quarter possession exchange executed in inventory records.",
          };
        }
        return rec;
      })
    );
    toast.success(`Mutual exchange executed. Quarter allotments successfully swapped!`);
  };

  const handleRejectRecord = (id: string) => {
    setRecords((prev) =>
      prev.map((rec) => {
        if (rec.id === id) {
          return {
            ...rec,
            status: "REJECTED",
            remarks: "Exchange request rejected per DMRC Staff Quarters Allotment Guidelines.",
          };
        }
        return rec;
      })
    );
    toast.info(`Mutual Exchange ${id} marked as Rejected.`);
  };

  // Scoped list of records based on role / user context
  const scopedRecords = useMemo(() => {
    if (isHrAdminMode) {
      return records;
    }
    const empPadded = padEmpNo(loggedInEmpNo);
    return records.filter((rec) => {
      const emp1No = padEmpNo(rec.emp1?.empNo || (rec as any).emp1Id);
      const emp2No = padEmpNo(rec.emp2?.empNo || (rec as any).emp2Id);
      return emp1No === empPadded || emp2No === empPadded;
    });
  }, [records, isHrAdminMode, loggedInEmpNo]);

  // Filtered list
  const filteredRecords = scopedRecords.filter((rec) => {
    if (activeTab === "PENDING" && rec.status !== "PENDING") return false;
    if (activeTab === "APPROVED" && rec.status !== "APPROVED") return false;
    if (activeTab === "EXECUTED" && rec.status !== "EXECUTED") return false;
    if (activeTab === "REJECTED" && rec.status !== "REJECTED") return false;

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      const match1 =
        rec.id.toLowerCase().includes(q) ||
        rec.referenceNo.toLowerCase().includes(q) ||
        rec.emp1.empNo.toLowerCase().includes(q) ||
        rec.emp1.name.toLowerCase().includes(q) ||
        rec.emp1.location.toLowerCase().includes(q);
      const match2 =
        rec.emp2.empNo.toLowerCase().includes(q) ||
        rec.emp2.name.toLowerCase().includes(q) ||
        rec.emp2.location.toLowerCase().includes(q);
      return match1 || match2;
    }
    return true;
  });

  // Print Annexure-IV Format
  const handlePrintAnnexure = () => {
    window.print();
  };

  // Open Form modal & auto-populate Employee 1 with logged-in employee details
  const handleOpenNewForm = async () => {
    setEmp1({ ...emptyEmpDetails });
    setEmp2({ ...emptyEmpDetails });
    setReason("");
    setApprovalRefNo("");
    setDeclarationAgreed(false);
    setExchangeType("DIFFERENT_LOCATIONS");

    if (!isHrAdminMode && loggedInEmpNo) {
      const isOccupying = await handleLookupEmployee(loggedInEmpNo, 1);
      if (!isOccupying) {
        // Do not open the mutual exchange form modal if the user is not occupying any staff quarters
        return;
      }
    }
    setFormModalOpen(true);
  };

  // Open preview modal
  const handleOpenPreview = (record: MutualExchangeRecord) => {
    setSelectedRecordForPreview(record);
    setPreviewModalOpen(true);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ti ti-arrows-exchange text-danger fs-3"></i> Mutual Exchange of Staff Quarters
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={isHrAdminMode ? (routes.staffQuarterAdmin?.dashboard || "#") : (routes.services?.list || routes.staffQuarter?.list)}>
                    {isHrAdminMode ? "Staff Quarters" : "Services"}
                  </Link>
                </li>
                {!isHrAdminMode && (
                  <li className="breadcrumb-item">
                    <Link to={routes.staffQuarter.list}>Staff Quarters</Link>
                  </li>
                )}
                {isHrAdminMode && (
                  <li className="breadcrumb-item">
                    <Link to={routes.staffQuarter.management.allotments || "#"}>Management</Link>
                  </li>
                )}
                <li className="breadcrumb-item active" aria-current="page">
                  Mutual Exchange (Annexure-IV)
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
              onClick={() => {
                const blank: MutualExchangeRecord = {
                  id: "BLANK-ANNEXURE-IV",
                  referenceNo: "DMRC/HR/SQ/ANNEXURE-IV",
                  exchangeType: "DIFFERENT_LOCATIONS",
                  emp1: {
                    empNo: "................",
                    name: "................................",
                    designation: "................................",
                    quarterNo: "................",
                    quarterType: "........",
                    pool: "................",
                    location: "................................",
                  },
                  emp2: {
                    empNo: "................",
                    name: "................................",
                    designation: "................................",
                    quarterNo: "................",
                    quarterType: "........",
                    pool: "................",
                    location: "................................",
                  },
                  reason: "",
                  status: "PENDING",
                  createdAt: new Date().toISOString(),
                };
                setSelectedRecordForPreview(blank);
                setPreviewModalOpen(true);
              }}
              style={{ borderRadius: "8px" }}
            >
              <i className="ti ti-file-download me-1"></i> Blank Annexure-IV
            </button>
            {!isHrAdminMode && (
              <button
                type="button"
                className="btn btn-danger btn-sm d-flex align-items-center gap-1 shadow-sm px-3"
                onClick={handleOpenNewForm}
                style={{ borderRadius: "8px", fontWeight: 600 }}
              >
                <i className="ti ti-plus me-1"></i> Apply for Mutual Exchange
              </button>
            )}
          </div>
        </div>

        {/* Stats Summary Cards */}
        <div className="row g-3 mb-4 align-items-stretch">
          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0 }}>
                  <span className="text-muted extra-small fw-bold text-uppercase d-block mb-1 text-truncate" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
                    Total Applications
                  </span>
                  <h4 className="mb-0 fw-bold text-dark">{scopedRecords.length}</h4>
                </div>
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center bg-primary-subtle text-primary flex-shrink-0 ms-2"
                  style={{ width: "42px", height: "42px" }}
                >
                  <i className="ti ti-files fs-4"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0 }}>
                  <span className="text-muted extra-small fw-bold text-uppercase d-block mb-1 text-truncate" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
                    Pending Review
                  </span>
                  <h4 className="mb-0 fw-bold text-warning">
                    {scopedRecords.filter((r) => r.status === "PENDING").length}
                  </h4>
                </div>
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center bg-warning-subtle text-warning flex-shrink-0 ms-2"
                  style={{ width: "42px", height: "42px" }}
                >
                  <i className="ti ti-clock-hour-4 fs-4"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0 }}>
                  <span className="text-muted extra-small fw-bold text-uppercase d-block mb-1 text-truncate" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
                    Approved Exchanges
                  </span>
                  <h4 className="mb-0 fw-bold text-success">
                    {scopedRecords.filter((r) => r.status === "APPROVED" || r.status === "EXECUTED").length}
                  </h4>
                </div>
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center bg-success-subtle text-success flex-shrink-0 ms-2"
                  style={{ width: "42px", height: "42px" }}
                >
                  <i className="ti ti-circle-check fs-4"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-12">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div style={{ minWidth: 0 }}>
                  <span className="text-muted extra-small fw-bold text-uppercase d-block mb-1 text-truncate" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
                    Standard Form
                  </span>
                  <h6 className="mb-0 fw-bold text-danger text-truncate">Annexure-IV</h6>
                </div>
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center bg-danger-subtle text-danger flex-shrink-0 ms-2"
                  style={{ width: "42px", height: "42px" }}
                >
                  <i className="ti ti-file-text fs-4"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Annexure-IV Format Banner */}
        <div
          className="card border-0 shadow-sm mb-4"
          style={{
            borderRadius: "14px",
            background: "linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)",
            borderLeft: "4px solid #E30613",
          }}
        >
          <div className="card-body p-3 p-md-4 d-flex flex-md-row flex-column align-items-start align-items-md-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm flex-shrink-0"
                style={{ width: "48px", height: "48px", border: "1px solid #fed7aa" }}
              >
                <i className="ti ti-file-certificate text-danger fs-3"></i>
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <span className="badge bg-danger text-white px-2 py-1 flex-shrink-0" style={{ fontSize: "10px", lineHeight: "1.2" }}>
                    DMRC STAFF QUARTERS POLICY
                  </span>
                  <span className="fw-bold text-dark fs-6" style={{ lineHeight: "1.3" }}>Annexure-IV: Format for Mutual Exchange</span>
                </div>
                <p className="text-muted small mb-0">
                  Allows two eligible employees occupying staff quarters to mutually exchange flats (at different locations or at same location) upon competent authority approval.
                </p>
              </div>
            </div>

            {!isHrAdminMode && (
              <button
                type="button"
                className="btn btn-danger btn-sm shadow-sm flex-shrink-0 px-3"
                onClick={handleOpenNewForm}
                style={{ borderRadius: "8px", fontWeight: 600 }}
              >
                <i className="ti ti-plus me-1"></i> Apply for Mutual Exchange
              </button>
            )}
          </div>
        </div>

        {/* Mutual Exchange Applications Table */}
        <div className="card border-0 shadow-sm" style={{ borderRadius: "14px" }}>
          <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2">
            {/* Tabs */}
            <ul className="nav nav-pills gap-1 flex-wrap">
              {[
                { key: "ALL", label: "All Exchanges" },
                { key: "PENDING", label: "Pending Review" },
                { key: "APPROVED", label: "Approved" },
                { key: "EXECUTED", label: "Executed / Swapped" },
                { key: "REJECTED", label: "Rejected" },
              ].map((tab) => (
                <li className="nav-item" key={tab.key}>
                  <button
                    type="button"
                    className={`nav-link btn-sm ${activeTab === tab.key ? "active bg-danger text-white" : "text-muted"}`}
                    onClick={() => setActiveTab(tab.key as any)}
                    style={{ borderRadius: "8px", fontSize: "12.5px" }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Search */}
            <div className="d-flex align-items-center gap-2">
              <div className="input-group input-group-sm" style={{ width: "260px" }}>
                <span className="input-group-text bg-light border-end-0">
                  <i className="ti ti-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search Emp ID, Name, Location..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive" style={{ overflowX: "auto" }}>
              <table className="table table-hover align-middle mb-0" style={{ minWidth: "900px" }}>
                <thead className="table-light">
                  <tr style={{ fontSize: "12px", textTransform: "uppercase" }}>
                    <th className="py-3 px-3" style={{ width: "15%" }}>Application Ref</th>
                    <th className="py-3 px-3" style={{ width: "13%" }}>Category</th>
                    <th className="py-3 px-3" style={{ width: "26%" }}>Employee 1 (Current Flat)</th>
                    <th className="py-3 px-3" style={{ width: "26%" }}>Employee 2 (Current Flat)</th>
                    <th className="py-3 px-3" style={{ width: "10%" }}>Date</th>
                    <th className="py-3 px-3 text-center" style={{ width: "5%" }}>Status</th>
                    <th className="py-3 px-3 text-end" style={{ width: "5%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13px" }}>
                  {recordsLoading || recordsError || filteredRecords.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        {recordsLoading ? <Spin tip="Loading mutual exchanges..." /> : recordsError ? (
                          <span className="text-danger" role="alert">{recordsError}</span>
                        ) : <>
                          <i className="ti ti-files-off fs-1 d-block mb-2 text-secondary"></i>
                          No mutual exchange applications found matching the criteria.
                        </>}
                      </td>
                    </tr>
                  ) : (
                    filteredRecords.map((rec) => (
                      <tr key={rec.id}>
                        <td className="px-3">
                          <span className="fw-bold text-dark d-block">{rec.id}</span>
                          <span className="text-muted extra-small">{rec.referenceNo}</span>
                        </td>
                        <td className="px-3">
                          <span
                            className={`badge ${rec.exchangeType === "DIFFERENT_LOCATIONS"
                              ? "bg-purple-subtle text-purple border border-purple-subtle"
                              : "bg-info-subtle text-info border border-info-subtle"
                              } px-2 py-1`}
                            style={{ fontSize: "11px" }}
                          >
                            {rec.exchangeType === "DIFFERENT_LOCATIONS"
                              ? "Different Locations"
                              : "Same Location"}
                          </span>
                        </td>
                        <td className="px-3">
                          <div className="fw-bold text-dark">{rec.emp1.name}</div>
                          <div className="text-muted extra-small">
                            Emp ID: <strong>{rec.emp1.empNo}</strong> &middot; {rec.emp1.designation}
                          </div>
                          <div className="text-danger small mt-1">
                            <i className="ti ti-home me-1"></i>
                            {rec.emp1.quarterNo} ({rec.emp1.location} - Type {rec.emp1.quarterType})
                          </div>
                          {rec.emp1.registrationReferenceNumber && (
                            <div className="text-muted extra-small mt-1">
                              Reg Ref: {rec.emp1.registrationReferenceNumber}
                            </div>
                          )}
                        </td>
                        <td className="px-3">
                          <div className="fw-bold text-dark">{rec.emp2.name}</div>
                          <div className="text-muted extra-small">
                            Emp ID: <strong>{rec.emp2.empNo}</strong> &middot; {rec.emp2.designation}
                          </div>
                          <div className="text-danger small mt-1">
                            <i className="ti ti-home me-1"></i>
                            {rec.emp2.quarterNo} ({rec.emp2.location} - Type {rec.emp2.quarterType})
                          </div>
                          {rec.emp2.registrationReferenceNumber && (
                            <div className="text-muted extra-small mt-1">
                              Reg Ref: {rec.emp2.registrationReferenceNumber}
                            </div>
                          )}
                        </td>
                        <td className="px-3">
                          <span className="text-dark">{formatDate(rec.createdAt)}</span>
                        </td>
                        <td className="px-3 text-center">
                          <span
                            className={`badge ${rec.status === "EXECUTED"
                              ? "bg-success text-white"
                              : rec.status === "APPROVED"
                                ? "bg-primary text-white"
                                : rec.status === "REJECTED"
                                  ? "bg-danger text-white"
                                  : "bg-warning text-dark"
                              } px-2 py-1`}
                            style={{ fontSize: "11px" }}
                          >
                            {rec.status === "EXECUTED"
                              ? "Executed"
                              : rec.status === "APPROVED"
                                ? "Approved"
                                : rec.status === "REJECTED"
                                  ? "Rejected"
                                  : "Pending Review"}
                          </span>
                        </td>
                        <td className="px-3 text-end">
                          <div className="d-flex align-items-center justify-content-end gap-1">
                            <Tooltip title="View request details">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm p-1 px-2"
                                aria-label="View request details"
                                onClick={() => setViewRecord(rec)}
                                style={{ borderRadius: "6px" }}
                              >
                                <i className="ti ti-eye"></i>
                              </button>
                            </Tooltip>
                            <Tooltip title="View & Print Annexure-IV">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm p-1 px-2"
                                onClick={() => handleOpenPreview(rec)}
                                style={{ borderRadius: "6px" }}
                              >
                                <i className="ti ti-printer"></i>
                              </button>
                            </Tooltip>

                            {isHrAdminMode && rec.status === "PENDING" && (
                              <Tooltip title="Approve Mutual Exchange">
                                <button
                                  type="button"
                                  className="btn btn-outline-success btn-sm p-1 px-2"
                                  onClick={() => handleApproveRecord(rec.id)}
                                  style={{ borderRadius: "6px" }}
                                >
                                  <i className="ti ti-check"></i>
                                </button>
                              </Tooltip>
                            )}

                            {isHrAdminMode && rec.status === "APPROVED" && (
                              <Tooltip title="Execute Quarter Swap">
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm p-1 px-2"
                                  onClick={() => handleExecuteSwap(rec.id)}
                                  style={{ borderRadius: "6px", fontSize: "11px" }}
                                >
                                  <i className="ti ti-arrows-exchange me-1"></i> Swap
                                </button>
                              </Tooltip>
                            )}

                            {isHrAdminMode && rec.status === "PENDING" && (
                              <Tooltip title="Reject Application">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm p-1 px-2 text-danger"
                                  onClick={() => handleRejectRecord(rec.id)}
                                  style={{ borderRadius: "6px" }}
                                >
                                  <i className="ti ti-x"></i>
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <Modal
          open={!!viewRecord}
          onCancel={() => setViewRecord(null)}
          width={900}
          centered
          title={
            <div className="d-flex align-items-center gap-3">
              <i className="ti ti-arrows-exchange text-danger fs-3" aria-hidden="true"></i>
              <div>
                <div className="fw-bold">Mutual Exchange Request Details</div>
                <div className="text-muted small fw-normal">Annexure-IV: Format for Mutual Exchange of Staff Quarters</div>
              </div>
            </div>
          }
          footer={<button type="button" className="btn btn-light" onClick={() => setViewRecord(null)}>Close</button>}
        >
          {viewRecord && (
            <div className="pt-3">
              <div className="rounded-3 border p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3" style={{ background: "#fff8f7", borderLeft: "3px solid #e31e24" }}>
                <div>
                  <div className="fw-bold mb-2">{viewRecord.referenceNo || viewRecord.id}</div>
                  <span className="badge bg-light text-primary border">
                    {viewRecord.exchangeType === "SAME_LOCATION" ? "Same Location" : "Different Locations"}
                  </span>
                  <div className="text-muted small mt-2">Submitted on: {formatDate(viewRecord.createdAt)}</div>
                </div>
                <span className={`badge ${viewRecord.status === "REJECTED" ? "bg-danger" : viewRecord.status === "PENDING" ? "bg-warning text-dark" : "bg-success"}`}>
                  {viewRecord.status === "PENDING" ? "Pending Review" : viewRecord.status === "EXECUTED" ? "Executed / Swapped" : viewRecord.status === "APPROVED" ? "Approved" : "Rejected"}
                </span>
              </div>

              <div className="row g-3 mb-3">
                {[viewRecord.emp1, viewRecord.emp2].map((employee, index) => (
                  <div className="col-12 col-md-6" key={index}>
                    <div className="border rounded-3 h-100 overflow-hidden">
                      <div className="p-3 border-bottom d-flex justify-content-between align-items-center gap-2" style={{ background: index === 0 ? "#fff5f5" : "#f0f7ff" }}>
                        <strong className={index === 0 ? "text-danger" : "text-primary"}>{index === 0 ? "Applicant" : "Second Party"}</strong>
                        <span className="badge bg-white text-dark border">{employee?.empNo || "—"}</span>
                      </div>
                      <div className="p-3">
                        <div className="fw-bold">{employee?.name || "—"}</div>
                        <div className="text-muted small mt-1">{[employee?.designation, employee?.department].filter(Boolean).join(" · ") || "—"}</div>
                        <dl className="bg-light rounded border p-2 my-3 small">
                          {[
                            ["Current Quarter", employee?.quarterNo],
                            ["Type / Pool", [employee?.quarterType, employee?.pool].filter(Boolean).join(" / ")],
                            ["Category", resolveFlatCategory(employee) || (employee as any)?.category || "UR"],
                            ["Location", employee?.location],
                            ...(["APPROVED", "EXECUTED"].includes(viewRecord.status) ? [
                              ["Old Flat Occupation Date", formatDate(employee?.lastOccupationDate)],
                              ["New Flat Occupation Date", formatDate(employee?.postExchangeOccupationDate)],
                            ] : []),
                          ].map(([label, value]) => (
                            <div className="d-flex justify-content-between gap-3 py-1" key={label}>
                              <dt className="text-muted fw-normal">{label}</dt>
                              <dd className="mb-0 text-end">{value || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                        <div className="small"><strong>Controlling Officer: </strong><span className="text-muted">{controllingOfficerLabel(viewRecord, employee, index)}</span></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border rounded-3 p-3 mb-3" style={{ background: "#fbfcff" }}>
                <div className="fw-bold mb-3"><i className="ti ti-arrows-exchange text-danger me-2" aria-hidden="true"></i>Proposed Mutual Accommodation Swap</div>
                <div className="row g-3 align-items-center text-center">
                  <div className="col-12 col-md-5"><div className="border rounded bg-white p-3"><div className="small text-muted mb-2">APPLICANT MOVES TO</div><strong className="text-danger">{viewRecord.emp2?.quarterNo || "—"}</strong><div>{viewRecord.emp2?.location || "—"}</div></div></div>
                  <div className="col-12 col-md-2"><i className="ti ti-arrows-exchange text-danger fs-3" aria-hidden="true"></i></div>
                  <div className="col-12 col-md-5"><div className="border rounded bg-white p-3"><div className="small text-muted mb-2">SECOND PARTY MOVES TO</div><strong className="text-danger">{viewRecord.emp1?.quarterNo || "—"}</strong><div>{viewRecord.emp1?.location || "—"}</div></div></div>
                </div>
              </div>

              <MutualExchangeHistory record={viewRecord} />
              <div className="border rounded-3 bg-light p-3 mb-3">
                <div className="fw-bold small mb-1">Reason / Grounds</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{viewRecord.reason || "—"}</div>
              </div>
              {viewRecord.remarks && <div className="border rounded-3 p-3"><div className="fw-bold small mb-1">Review Remarks</div><div style={{ whiteSpace: "pre-wrap" }}>{viewRecord.remarks}</div></div>}
            </div>
          )}
        </Modal>

        {/* Modal 1: Initiate / Apply New Mutual Exchange Form */}
        <Modal
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-arrows-exchange text-danger fs-4"></i>
              <span className="fw-bold">
                {isHrAdminMode
                  ? "Initiate Mutual Exchange of Staff Quarters (Annexure-IV)"
                  : "Apply for Mutual Exchange of Staff Quarters (Annexure-IV)"}
              </span>
            </div>
          }
          open={formModalOpen}
          onCancel={() => setFormModalOpen(false)}
          width={900}
          centered
          zIndex={1060}
          footer={[
            <button
              key="back"
              type="button"
              className="btn btn-light border px-4"
              onClick={() => setFormModalOpen(false)}
              disabled={submitting}
            >
              Cancel
            </button>,
            <button
              key="submit"
              type="button"
              className="btn btn-danger px-4"
              onClick={handleCreateApplication}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spin size="small" /> Processing...
                </>
              ) : (
                <>
                  <i className="ti ti-send me-1"></i> Submit Application & Generate Annexure-IV
                </>
              )}
            </button>,
          ]}
        >
          <div className="py-2">
            {/* Tick only one: Exchange Type */}
            <div className="p-3 bg-light rounded-3 border mb-4">
              <label className="form-label small fw-bold text-dark d-block mb-2">
                Category of Mutual Exchange
              </label>
              <div className="d-flex flex-wrap gap-4">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="exchangeTypeRadio"
                    id="typeDiffLoc"
                    checked={exchangeType === "DIFFERENT_LOCATIONS"}
                    onChange={() => setExchangeType("DIFFERENT_LOCATIONS")}
                  />
                  <label className="form-check-label fw-bold text-dark" htmlFor="typeDiffLoc">
                    Mutual exchange at different locations
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="exchangeTypeRadio"
                    id="typeSameLoc"
                    checked={exchangeType === "SAME_LOCATION"}
                    onChange={() => setExchangeType("SAME_LOCATION")}
                  />
                  <label className="form-check-label fw-bold text-dark" htmlFor="typeSameLoc">
                    Mutual exchange at same location
                  </label>
                </div>
              </div>
            </div>

            {/* Annexure-IV Side-by-Side Table Form */}
            <div className="table-responsive mb-4" style={{ overflowX: "auto" }}>
              <table className="table table-bordered align-middle mb-0" style={{ borderColor: "#cbd5e1", minWidth: "700px", tableLayout: "fixed", width: "100%" }}>
                <thead style={{ backgroundColor: "#f1f5f9" }}>
                  <tr className="text-dark fw-bold" style={{ fontSize: "13px" }}>
                    <th style={{ width: "7%" }} className="text-center">
                      S. N.
                    </th>
                    <th style={{ width: "25%" }}>Particulars</th>
                    <th style={{ width: "34%" }}>
                      <span className="text-danger">Appicant</span>
                    </th>
                    <th style={{ width: "34%" }}>
                      <span className="text-primary">Details of Emp. 2 (Second Party)</span>
                    </th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: "13px" }}>
                  {/* Row 1: Name of Employee */}
                  <tr>
                    <td className="text-center fw-bold">1</td>
                    <td className="fw-semibold">Name of Employee</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Name..."
                        value={emp1.name}
                        disabled
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Name..."
                        value={emp2.name}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>

                  {/* Row 2: Emp. No. */}
                  <tr>
                    <td className="text-center fw-bold">2</td>
                    <td className="fw-semibold">
                      Emp. No. <span className="text-danger">*</span>
                    </td>
                    <td>
                      {!isHrAdminMode ? (
                        <div>
                          <input
                            type="text"
                            className="form-control form-control-sm bg-light"
                            value={emp1.empNo}
                            disabled
                            readOnly
                          />
                          <span className="badge bg-danger-subtle text-danger mt-1" style={{ fontSize: "10px" }}>
                            Logged-in Applicant (Read-only)
                          </span>
                        </div>
                      ) : (
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Enter Emp ID & Search..."
                            value={emp1.empNo}
                            onChange={(e) => setEmp1({ ...emp1, empNo: e.target.value })}
                            onBlur={(e) => handleLookupEmployee(e.target.value, 1)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleLookupEmployee(emp1.empNo, 1);
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleLookupEmployee(emp1.empNo, 1)}
                            title="Search Employee 1 Quarter Info"
                          >
                            {searchingEmp1 ? <Spin size="small" /> : <i className="ti ti-search"></i>}
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Enter Emp ID & Search..."
                          value={emp2.empNo}
                          onChange={(e) => setEmp2({ ...emp2, empNo: e.target.value })}
                          onBlur={(e) => handleLookupEmployee(e.target.value, 2)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleLookupEmployee(emp2.empNo, 2);
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleLookupEmployee(emp2.empNo, 2)}
                          title="Search Employee 2 Quarter Info & Check Compatibility"
                        >
                          {searchingEmp2 ? <Spin size="small" /> : <i className="ti ti-search"></i>}
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Row 3: Designation */}
                  <tr>
                    <td className="text-center fw-bold">3</td>
                    <td className="fw-semibold">Designation</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Designation..."
                        value={emp1.designation}
                        disabled
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Designation..."
                        value={emp2.designation}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>

                  {/* Row 4: Allotted staff quarter no. (Block - Flat) */}
                  <tr>
                    <td className="text-center fw-bold">4</td>
                    <td className="fw-semibold">
                      Allotted staff quarter no.
                      <span className="text-muted extra-small d-block fw-normal">(Block - Flat No)</span>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Flat (e.g. B - 901)..."
                        value={emp1.quarterNo}
                        disabled
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Flat (e.g. C - 304)..."
                        value={emp2.quarterNo}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>

                  {/* Row 5: Type of quarter (Input, disabled) */}
                  <tr>
                    <td className="text-center fw-bold">5</td>
                    <td className="fw-semibold">Type of quarter</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Quarter Type..."
                        value={emp1.quarterType}
                        disabled
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Quarter Type..."
                        value={emp2.quarterType}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>

                  {/* Row 6: Pool of quarter (Input, disabled) */}
                  <tr>
                    <td className="text-center fw-bold">6</td>
                    <td className="fw-semibold">Pool of quarter</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Pool..."
                        value={emp1.pool}
                        disabled
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Pool..."
                        value={emp2.pool}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>

                  {/* Row 7: Location of quarter */}
                  <tr>
                    <td className="text-center fw-bold">7</td>
                    <td className="fw-semibold">Location of quarter</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Location..."
                        value={emp1.location}
                        disabled
                        readOnly
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm bg-light"
                        placeholder="Auto-populated Location..."
                        value={emp2.location}
                        disabled
                        readOnly
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Additional Metadata Cards: Registration, Allotment, Occupation Ref & Date */}
            <div className="card border shadow-none bg-light-subtle rounded-3 mb-4">
              <div className="card-header bg-white py-2 px-3 border-bottom d-flex align-items-center justify-content-between">
                <span className="fw-bold text-dark small">
                  <i className="ti ti-id-badge-2 text-danger me-1"></i> Reference Numbers & Key Milestone Dates
                </span>
                <span className="badge bg-light text-muted border" style={{ fontSize: "10px" }}>
                  Auto-populated from Staff Quarters Registry
                </span>
              </div>

              <div className="card-body p-3">
                <div className="row g-3">
                  {/* Emp 1 References */}
                  <div className="col-md-6 col-12 border-end-md">
                    <div className="fw-bold text-danger small mb-2 d-flex align-items-center justify-content-between">
                      <span>Emp 1: {emp1.name || "Employee 1"}</span>
                      <span className="badge bg-danger-subtle text-danger">{emp1.empNo || "ID"}</span>
                    </div>

                    <div className="row g-2 extra-small text-muted" style={{ fontSize: "11.5px" }}>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Registration Ref:</span>
                        <span>{emp1.registrationReferenceNumber || "N/A"}</span>
                      </div>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Registration Date:</span>
                        <span>{formatDate(emp1.registrationDate)}</span>
                      </div>

                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Allotment Ref:</span>
                        <span>{emp1.allotmentReferenceNumber || "N/A"}</span>
                      </div>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Allotment Date:</span>
                        <span>{formatDate(emp1.allotmentDate)}</span>
                      </div>

                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Occupation Ref:</span>
                        <span>{emp1.occupationReferenceNumber || "N/A"}</span>
                      </div>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Occupation Date:</span>
                        <span>{formatDate(emp1.occupationDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Emp 2 References */}
                  <div className="col-md-6 col-12">
                    <div className="fw-bold text-primary small mb-2 d-flex align-items-center justify-content-between">
                      <span>Emp 2: {emp2.name || "Employee 2"}</span>
                      <span className="badge bg-primary-subtle text-primary">{emp2.empNo || "ID"}</span>
                    </div>

                    <div className="row g-2 extra-small text-muted" style={{ fontSize: "11.5px" }}>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Registration Ref:</span>
                        <span>{emp2.registrationReferenceNumber || "N/A"}</span>
                      </div>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Registration Date:</span>
                        <span>{formatDate(emp2.registrationDate)}</span>
                      </div>

                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Allotment Ref:</span>
                        <span>{emp2.allotmentReferenceNumber || "N/A"}</span>
                      </div>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Allotment Date:</span>
                        <span>{formatDate(emp2.allotmentDate)}</span>
                      </div>

                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Occupation Ref:</span>
                        <span>{emp2.occupationReferenceNumber || "N/A"}</span>
                      </div>
                      <div className="col-6">
                        <span className="d-block fw-bold text-dark">Occupation Date:</span>
                        <span>{formatDate(emp2.occupationDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Joint Undertaking Declaration */}
            <div className="form-check p-3 bg-light-subtle rounded-3 border mb-2">
              <input
                className="form-check-input ms-0 me-2"
                type="checkbox"
                id="declarationCheckbox"
                checked={declarationAgreed}
                onChange={(e) => setDeclarationAgreed(e.target.checked)}
              />
              <label className="form-check-label small text-dark" htmlFor="declarationCheckbox">
                <strong>Joint Declaration:</strong> I hereby certify that the information provided is correct, and request mutual exchange of allotted staff quarters subject to terms & conditions of DMRC Staff Quarters Allotment Rules.
              </label>
            </div>
            <div className="text-muted extra-small ms-1">
              <i className="ti ti-info-circle me-1 text-primary"></i>
              Note: Submitting this application will initiate the mutual exchange request into DMRC's review workflow. Physical verification and consent will be processed by Competent Authority.
            </div>
          </div>
        </Modal>

        {/* Modal: Incompatibility Warning Popup */}
        <Modal
          title={
            <div className="d-flex align-items-center gap-2 text-danger">
              <i className="ti ti-alert-triangle fs-3 text-danger"></i>
              <span className="fw-bold">Incompatible for Mutual Exchange</span>
            </div>
          }
          open={incompatibleModalOpen}
          onCancel={() => setIncompatibleModalOpen(false)}
          centered
          width={560}
          zIndex={1065}
          footer={[
            <button
              key="close"
              type="button"
              className="btn btn-danger px-4"
              onClick={() => setIncompatibleModalOpen(false)}
            >
              Understood
            </button>,
          ]}
        >
          <div className="py-2">
            <div
              className="alert alert-danger d-flex align-items-start gap-2 mb-3"
              style={{ borderRadius: "8px", fontSize: "13px" }}
            >
              <i className="ti ti-ban fs-4 text-danger flex-shrink-0 mt-1"></i>
              <div>
                <strong>Policy Restriction:</strong> {incompatibilityData.message}
              </div>
            </div>

            <p className="text-secondary small mb-3">
              According to DMRC Staff Quarters Allotment Policy, mutual exchange can only take place between quarters of the <strong>exact same Pool</strong> and <strong>same Quarter Type (Category)</strong>.
            </p>

            {incompatibilityData.emp1Info && incompatibilityData.emp2Info && (
              <div className="table-responsive rounded-3 border">
                <table className="table table-bordered table-sm mb-0 align-middle" style={{ fontSize: "12px" }}>
                  <thead className="table-light">
                    <tr>
                      <th>Particular</th>
                      <th className="text-danger">Employee 1</th>
                      <th className="text-primary">Employee 2</th>
                      <th className="text-center">Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-bold">Quarter Type</td>
                      <td className="fw-semibold">{incompatibilityData.emp1Info.quarterType || "N/A"}</td>
                      <td className="fw-semibold">{incompatibilityData.emp2Info.quarterType || "N/A"}</td>
                      <td className="text-center">
                        {String(incompatibilityData.emp1Info.quarterType || "").toUpperCase() ===
                          String(incompatibilityData.emp2Info.quarterType || "").toUpperCase() ? (
                          <span className="badge bg-success-subtle text-success">Same</span>
                        ) : (
                          <span className="badge bg-danger-subtle text-danger">Mismatch</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Pool of Quarter</td>
                      <td className="fw-semibold">{incompatibilityData.emp1Info.pool || "N/A"}</td>
                      <td className="fw-semibold">{incompatibilityData.emp2Info.pool || "N/A"}</td>
                      <td className="text-center">
                        {String(incompatibilityData.emp1Info.pool || "").toUpperCase() ===
                          String(incompatibilityData.emp2Info.pool || "").toUpperCase() ? (
                          <span className="badge bg-success-subtle text-success">Same</span>
                        ) : (
                          <span className="badge bg-danger-subtle text-danger">Mismatch</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Allotted Flat</td>
                      <td>{incompatibilityData.emp1Info.quarterNo || "N/A"}</td>
                      <td>{incompatibilityData.emp2Info.quarterNo || "N/A"}</td>
                      <td className="text-center text-muted">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal>

        {/* Modal 2: Official Printable Annexure-IV Format (Exact Replica) */}
        <Modal
          title={
            <div className="d-flex align-items-center justify-content-between pe-4">
              <span className="fw-bold">Official Annexure-IV Document Preview</span>
              <button
                type="button"
                className="btn btn-danger btn-sm shadow-sm"
                onClick={handlePrintAnnexure}
                style={{ borderRadius: "6px" }}
              >
                <i className="ti ti-printer me-1"></i> Print / Download PDF
              </button>
            </div>
          }
          open={previewModalOpen}
          onCancel={() => setPreviewModalOpen(false)}
          width={880}
          centered
          zIndex={1060}
          footer={[
            <button
              key="close"
              type="button"
              className="btn btn-light border px-4"
              onClick={() => setPreviewModalOpen(false)}
            >
              Close
            </button>,
            <button
              key="print"
              type="button"
              className="btn btn-danger px-4"
              onClick={handlePrintAnnexure}
            >
              <i className="ti ti-printer me-1"></i> Print Annexure-IV
            </button>,
          ]}
        >
          {selectedRecordForPreview && (
            <div
              ref={printAreaRef}
              className="p-4 bg-white text-dark"
              style={{
                fontFamily: "'Times New Roman', Times, serif",
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#000",
              }}
            >
              {/* Header Right: Annexure-IV */}
              <div className="d-flex justify-content-end mb-3">
                <span className="fw-bold" style={{ fontSize: "16px" }}>
                  Annexure-IV
                </span>
              </div>

              {/* Title Centered & Underlined */}
              <div className="text-center mb-4">
                <h4
                  className="fw-bold mb-0 text-dark"
                  style={{
                    textDecoration: "underline",
                    fontSize: "18px",
                    letterSpacing: "0.3px",
                  }}
                >
                  Format for Mutual Exchange of Staff Quarters
                </h4>
              </div>

              {/* Tick only one Checkbox Section */}
              <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                <div className="fw-bold">
                  Tick only one:{" "}
                  <span className="fw-normal">
                    Mutual exchange at different locations
                  </span>{" "}
                  <span
                    className="border border-dark d-inline-block text-center fw-bold ms-1"
                    style={{
                      width: "26px",
                      height: "26px",
                      lineHeight: "24px",
                      verticalAlign: "middle",
                    }}
                  >
                    {selectedRecordForPreview.exchangeType === "DIFFERENT_LOCATIONS" ? "✓" : ""}
                  </span>
                </div>

                <div>
                  <span className="fw-normal">
                    Mutual exchange at same location
                  </span>{" "}
                  <span
                    className="border border-dark d-inline-block text-center fw-bold ms-1"
                    style={{
                      width: "26px",
                      height: "26px",
                      lineHeight: "24px",
                      verticalAlign: "middle",
                    }}
                  >
                    {selectedRecordForPreview.exchangeType === "SAME_LOCATION" ? "✓" : ""}
                  </span>
                </div>
              </div>

              {/* Table Structure (Exact 1:1 format) */}
              <table
                className="table table-bordered border-dark mb-4"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  color: "#000",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <th
                      style={{
                        width: "8%",
                        textAlign: "center",
                        border: "1px solid #000",
                        padding: "8px",
                      }}
                      className="fw-bold"
                    >
                      S. N.
                    </th>
                    <th
                      style={{
                        width: "32%",
                        border: "1px solid #000",
                        padding: "8px",
                      }}
                      className="fw-bold"
                    >
                      Particulars
                    </th>
                    <th
                      style={{
                        width: "30%",
                        textAlign: "center",
                        border: "1px solid #000",
                        padding: "8px",
                      }}
                      className="fw-bold"
                    >
                      Details of Emp. 1
                    </th>
                    <th
                      style={{
                        width: "30%",
                        textAlign: "center",
                        border: "1px solid #000",
                        padding: "8px",
                      }}
                      className="fw-bold"
                    >
                      Details of Emp. 2
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* 1 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>1</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Name of Employee</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }} className="fw-semibold">
                      {selectedRecordForPreview.emp1.name || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }} className="fw-semibold">
                      {selectedRecordForPreview.emp2.name || ""}
                    </td>
                  </tr>

                  {/* 2 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>2</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Emp. No.</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp1.empNo || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp2.empNo || ""}
                    </td>
                  </tr>

                  {/* 3 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>3</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Designation</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp1.designation || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp2.designation || ""}
                    </td>
                  </tr>

                  {/* 4 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>4</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Allotted staff quarter no.</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp1.quarterNo || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp2.quarterNo || ""}
                    </td>
                  </tr>

                  {/* 5 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>5</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Type of quarter</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp1.quarterType || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp2.quarterType || ""}
                    </td>
                  </tr>

                  {/* 6 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>6</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Pool of quarter</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp1.pool || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp2.pool || ""}
                    </td>
                  </tr>

                  {/* 7 */}
                  <tr>
                    <td style={{ textAlign: "center", border: "1px solid #000", padding: "8px" }}>7</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>Location of quarter</td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp1.location || ""}
                    </td>
                    <td style={{ border: "1px solid #000", padding: "8px" }}>
                      {selectedRecordForPreview.emp2.location || ""}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Declarations & Signatures Section */}
              <div className="mt-5 pt-3">
                <div className="row g-4 text-center">
                  <div className="col-6">
                    <div className="border-bottom border-dark pb-2 mb-2" style={{ minHeight: "40px" }}></div>
                    <div className="fw-bold">Signature of Employee 1</div>
                    <div className="extra-small text-muted">
                      Name: {selectedRecordForPreview.emp1.name || "................................"}
                    </div>
                    <div className="extra-small text-muted">
                      Emp No: {selectedRecordForPreview.emp1.empNo || "................"}
                    </div>
                    <div className="extra-small text-muted">Date: ................................</div>
                  </div>

                  <div className="col-6">
                    <div className="border-bottom border-dark pb-2 mb-2" style={{ minHeight: "40px" }}></div>
                    <div className="fw-bold">Signature of Employee 2</div>
                    <div className="extra-small text-muted">
                      Name: {selectedRecordForPreview.emp2.name || "................................"}
                    </div>
                    <div className="extra-small text-muted">
                      Emp No: {selectedRecordForPreview.emp2.empNo || "................"}
                    </div>
                    <div className="extra-small text-muted">Date: ................................</div>
                  </div>
                </div>

                <div className="row g-4 text-center mt-4 pt-3 border-top border-secondary">
                  <div className="col-6">
                    <div className="border-bottom border-dark pb-2 mb-2" style={{ minHeight: "35px" }}></div>
                    <div className="fw-bold">Forwarded by Controlling Officer 1</div>
                    <div className="extra-small text-muted">Designation & Stamp</div>
                  </div>

                  <div className="col-6">
                    <div className="border-bottom border-dark pb-2 mb-2" style={{ minHeight: "35px" }}></div>
                    <div className="fw-bold">Forwarded by Controlling Officer 2</div>
                    <div className="extra-small text-muted">Designation & Stamp</div>
                  </div>
                </div>

                <div className="mt-5 pt-3 text-center">
                  <div className="d-inline-block border p-3 px-5 text-center bg-light">
                    <div className="fw-bold text-uppercase" style={{ letterSpacing: "1px" }}>
                      Approved by Competent Authority / HR Admin
                    </div>
                    <div className="small text-muted mt-1">
                      Reference: {selectedRecordForPreview.referenceNo}
                    </div>
                    {selectedRecordForPreview.approvedAt && (
                      <div className="small text-success mt-1">
                        Approved on: {formatDate(selectedRecordForPreview.approvedAt)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default MutualExchange;
